import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { getERPClient } from "@/lib/erp/client";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const UPDATE_METHOD =
  "zaviago_backend.storefront.middleware_connector.update_product_specs";

interface ExogroSpecsCardProps {
  itemCode: string;
  initial: {
    country: string;
    warranty: string;
    orderUnit: string;
    locked: boolean;
  };
}

/**
 * Exogro-only product-spec editor (ประเทศผู้ผลิต / การรับประกัน / สั่งครั้งละ).
 * Rendered on the item detail ONLY for the Exogro tenant. Saving locks the specs so
 * the KTW auto-sync won't overwrite the manual edit; "resume auto-sync" clears the lock.
 * The backend endpoint is itself gated to Exogro middleware items.
 */
export function ExogroSpecsCard({ itemCode, initial }: ExogroSpecsCardProps) {
  const qc = useQueryClient();
  const [country, setCountry] = useState(initial.country || "");
  const [warranty, setWarranty] = useState(initial.warranty || "");
  const [orderUnit, setOrderUnit] = useState(initial.orderUnit || "");
  const [locked, setLocked] = useState(!!initial.locked);

  const saveMutation = useMutation({
    mutationFn: () =>
      getERPClient().call<{ locked: number }>(UPDATE_METHOD, {
        item_code: itemCode,
        country,
        warranty,
        order_unit: orderUnit,
      }),
    onSuccess: () => {
      setLocked(true);
      toast.success("บันทึกข้อมูลสินค้าแล้ว (ล็อกไม่ให้ระบบเขียนทับ)");
      qc.invalidateQueries({ queryKey: ["item", itemCode] });
    },
    onError: (e: unknown) =>
      toast.error(`บันทึกไม่สำเร็จ: ${e instanceof Error ? e.message : "error"}`),
  });

  const unlockMutation = useMutation({
    mutationFn: () =>
      getERPClient().call(UPDATE_METHOD, { item_code: itemCode, unlock: 1 }),
    onSuccess: () => {
      setLocked(false);
      toast.success("กลับไปดึงข้อมูลจาก KTW อัตโนมัติแล้ว");
      qc.invalidateQueries({ queryKey: ["item", itemCode] });
    },
    onError: (e: unknown) =>
      toast.error(`ไม่สำเร็จ: ${e instanceof Error ? e.message : "error"}`),
  });

  const busy = saveMutation.isPending || unlockMutation.isPending;

  return (
    <Card className="mb-4">
      <CardHeader className="pb-3">
        <CardTitle className="text-base">ข้อมูลสินค้า (KTW)</CardTitle>
        <CardDescription>
          {locked
            ? "ล็อกไว้แล้ว — แก้ด้วยมือ ระบบจะไม่เขียนทับ กด “กลับไปซิงค์อัตโนมัติ” เพื่อดึงจาก KTW ใหม่"
            : "ดึงจาก KTW อัตโนมัติ — แก้ไขแล้วบันทึกเพื่อล็อกค่าไว้"}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <div className="space-y-1">
            <Label htmlFor="exo-country">ประเทศผู้ผลิต</Label>
            <Input id="exo-country" value={country} disabled={busy}
              onChange={(e) => setCountry(e.target.value)} placeholder="-" />
          </div>
          <div className="space-y-1">
            <Label htmlFor="exo-warranty">การรับประกัน</Label>
            <Input id="exo-warranty" value={warranty} disabled={busy}
              onChange={(e) => setWarranty(e.target.value)} placeholder="-" />
          </div>
          <div className="space-y-1">
            <Label htmlFor="exo-order-unit">สั่งครั้งละ</Label>
            <Input id="exo-order-unit" value={orderUnit} disabled={busy}
              onChange={(e) => setOrderUnit(e.target.value)} placeholder="-" />
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" disabled={busy} onClick={() => saveMutation.mutate()}>
            {saveMutation.isPending ? "กำลังบันทึก..." : "บันทึก"}
          </Button>
          {locked && (
            <Button size="sm" variant="outline" disabled={busy}
              onClick={() => unlockMutation.mutate()}>
              {unlockMutation.isPending ? "..." : "กลับไปซิงค์อัตโนมัติ"}
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
